import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const BannerContainer = styled.div`
  position: fixed;
  top: 276px; /* Below header + closure + event (opening-status banner removed) */
  left: 0;
  right: 0;
  background: #8B2635;
  color: white;
  padding: 0.8rem 2rem;
  z-index: 100; /* Lower than navbar but higher than content */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border-bottom: 2px solid #d4af37;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideDown 0.5s ease-out forwards;
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    top: 264px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileInfoButton = styled.button`
  display: none;
  position: fixed;
  bottom: 140px;
  right: 80px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #8B2635;
  border: 2px solid #d4af37;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ModalOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const ModalContent = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #8B2635;
  color: white;
  padding: 1.5rem;
  border-radius: 20px 20px 0 0;
  transform: translateY(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-out;
  z-index: 1002;

  @media (max-width: 768px) {
    display: block;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Icon = styled.div`
  font-size: 1.2rem;
  color: #d4af37;
`;

const TextContent = styled.div`
  font-size: 1rem;
  line-height: 1.4;
  
  p {
    margin: 0;
  }
`;

const ImportantText = styled.span`
  font-weight: 600;
  color: #d4af37;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ChristmasBookingBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [shouldShow, setShouldShow] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Set end date for when the issue is resolved
    const issueEndDate = new Date('2025-12-31');
    const currentDate = new Date();
    
    // Hide banner if current date is after issue end date
    if (currentDate > issueEndDate) {
      setShouldShow(false);
    }
  }, []);

  if (!isVisible || !shouldShow) return null;

  return (
    <>
      <BannerContainer>
        <BannerContent>
          <InfoSection>
            <Icon>
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </Icon>
            <TextContent>
              <p>
                <ImportantText>Christmas Booking Notice:</ImportantText> We are currently experiencing some issues with Christmas bookings and are working hard to fix the issue. Please try again soon. We apologize for any inconvenience.
              </p>
            </TextContent>
          </InfoSection>
          <CloseButton onClick={() => setIsVisible(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </BannerContent>
      </BannerContainer>

      <MobileInfoButton onClick={() => setIsModalOpen(true)}>
        <FontAwesomeIcon icon={faExclamationTriangle} />
      </MobileInfoButton>

      <ModalOverlay $isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent $isOpen={isModalOpen} onClick={e => e.stopPropagation()}>
          <BannerContent>
            <InfoSection>
              <Icon>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </Icon>
              <TextContent>
                <p>
                  <ImportantText>Christmas Booking Notice:</ImportantText> We are currently experiencing some issues with Christmas bookings and are working hard to fix the issue. Please try again soon. We apologise for any inconvenience.
                </p>
              </TextContent>
            </InfoSection>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </BannerContent>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default ChristmasBookingBanner;

